'use client';

import { c as _c } from "react/compiler-runtime";
import { createTransactionTraceId, createTxError, type SubmissionResult, TxErrorCode, useAuth, useGetLeaderboardRank } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { D6_SCALE } from '@/config/solana';
import { TOAST_IDS } from '@/config/toast-ids';
import { useFeatureGate } from '@/hooks/use-feature-gate';
import { useFormattedCurrency } from '@/lib/currency';
import { useUserNetwork } from '@/lib/network/client';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';

/**
 * Devnet-only cap on USDC a user may back per rolling day. Devnet USDC is free,
 * so bots were backing and graduating every proposal (MOR-2368).
 */
const DAILY_BACKING_LIMIT_USDC = 2_000;
const DAILY_BACKING_LIMIT_D6: bigint = BigInt(DAILY_BACKING_LIMIT_USDC * D6_SCALE);
const ZERO_D6: bigint = BigInt(0);
export interface DailyBackingLimit {
  /** Re-reads today's total, then shows the limit snackbar and resolves true when this back would exceed the cap. */
  blocks: (usdcAmount: number, proposalId: string) => Promise<boolean>;
  /** Counts a confirmed back until the indexed total catches up with it. */
  record: (usdcAmount: number) => void;
}

/** Confirmed backs the indexer may not have counted yet, on top of the total read before them. */
interface UnindexedBacks {
  baseD6: bigint;
  addedD6: bigint;
}
export function useDailyBackingLimit() {
  const $ = _c(17);
  const tToasts = useTranslations("launchpad.toasts");
  const {
    formatBalance
  } = useFormattedCurrency();
  const {
    user
  } = useAuth();
  const {
    network
  } = useUserNetwork();
  const gateOn = useFeatureGate("limit_devnet_backing") === true;
  const userId = user?.id ?? "";
  const enabled = gateOn && network !== "mainnet" && userId !== "";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["totalBackedNetD6"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== userId) {
    t1 = {
      kind: "backers",
      userId,
      window: "1d",
      fields: t0
    };
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== enabled) {
    t2 = {
      enabled
    };
    $[3] = enabled;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const rankQuery = useGetLeaderboardRank(t1, t2);
  const lastIndexedD6 = useRef(ZERO_D6);
  const unindexed = useRef(null);
  let t3;
  if ($[5] !== rankQuery) {
    t3 = async function readBackedTodayD6() {
      const {
        data
      } = await rankQuery.refetch();
      const indexedD6 = BigInt(data?.data[0]?.totalBackedNetD6 ?? "0");
      lastIndexedD6.current = indexedD6;
      const pending = unindexed.current;
      if (pending === null) {
        return indexedD6;
      }
      const localD6 = pending.baseD6 + pending.addedD6;
      if (indexedD6 >= localD6) {
        unindexed.current = null;
        return indexedD6;
      }
      return localD6;
    };
    $[5] = rankQuery;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const readBackedTodayD6 = t3;
  let t4;
  if ($[7] !== enabled || $[8] !== formatBalance || $[9] !== readBackedTodayD6 || $[10] !== tToasts) {
    t4 = async (usdcAmount, proposalId) => {
      if (!enabled) {
        return false;
      }
      const backedTodayD6 = await readBackedTodayD6();
      if (backedTodayD6 + toD6(usdcAmount) <= DAILY_BACKING_LIMIT_D6) {
        return false;
      }
      logger.warn("proposal_back.daily_limit_reached", {
        proposalId,
        limitUsdc: DAILY_BACKING_LIMIT_USDC
      });
      showToast({
        id: TOAST_IDS.PROPOSAL_BACK_ERROR,
        message: tToasts("dailyBackingLimit", {
          amount: formatBalance(DAILY_BACKING_LIMIT_USDC)
        }),
        variant: "warning"
      });
      return true;
    };
    $[7] = enabled;
    $[8] = formatBalance;
    $[9] = readBackedTodayD6;
    $[10] = tToasts;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== enabled) {
    t5 = usdcAmount_0 => {
      if (!enabled) {
        return;
      }
      const pending_0 = unindexed.current;
      unindexed.current = {
        baseD6: pending_0?.baseD6 ?? lastIndexedD6.current,
        addedD6: (pending_0?.addedD6 ?? ZERO_D6) + toD6(usdcAmount_0)
      };
    };
    $[12] = enabled;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = {
      blocks: t4,
      record: t5
    };
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}

/** A back stopped by the cap never reached the chain, so it settles as a non-ambiguous build failure. */
export function limitBlockedResult(): SubmissionResult {
  const traceId = createTransactionTraceId();
  return {
    ok: false,
    traceId,
    error: createTxError(TxErrorCode.TX_BUILD_FAILED, traceId, 'building')
  };
}
function toD6(usdcAmount: number): bigint {
  return BigInt(Math.floor(usdcAmount * D6_SCALE));
}